import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import { toast } from 'react-hot-toast';
import { 
  Settings as SettingsIcon, 
  Save,
  Edit,
  Shield,
  Bell,
  Database,
  Mail,
  Smartphone,
  Globe,
  Server,
  Key,
  Lock
} from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';

const Settings = () => {
  const { hasPermission, user } = useAuth();
  const [activeTab, setActiveTab] = useState('system');
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    system: {
      appName: 'Public Rides Admin',
      version: '1.0.0',
      maintenanceMode: false,
      maxFileSize: 5242880,
      allowedFileTypes: ['jpg', 'jpeg', 'png', 'pdf']
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true
    },
    security: {
      sessionTimeout: 3600,
      maxLoginAttempts: 5,
      lockoutDuration: 1800,
      requireTwoFactor: false
    },
    api: {
      corsEnabled: true,
      rateLimiter: {
        enabled: true,
        requestsPerSecond: 10,
        burstLimit: 20,
        windowMs: 900000,
        skipSuccessfulRequests: false,
        skipFailedRequests: false,
        keyGenerator: 'ip', // 'ip', 'user', 'custom'
        message: 'Too many requests, please try again later.',
        statusCode: 429,
        headers: true,
        retryAfter: true
      }
    }
  });

  const { data, isLoading, error, refetch } = useQuery(
    ['settings'],
    () => api.get('/settings'),
    {
      keepPreviousData: true,
      onSuccess: (data) => {
        // Ensure rateLimiter object exists in the loaded data
        if (data?.data?.api && !data.data.api.rateLimiter) {
          data.data.api.rateLimiter = {
            enabled: true,
            requestsPerSecond: 10,
            burstLimit: 20,
            windowMs: 900000,
            skipSuccessfulRequests: false,
            skipFailedRequests: false,
            keyGenerator: 'ip',
            message: 'Too many requests, please try again later.',
            statusCode: 429,
            headers: true,
            retryAfter: true
          };
        }
      }
    }
  );

  const updateMutation = useMutation(
    (data) => api.put('/settings', data),
    {
      onSuccess: () => {
        toast.success('Settings updated successfully');
        refetch();
        setIsEditing(false);
      },
      onError: () => {
        toast.error('Failed to update settings');
      }
    }
  );

  const handleInputChange = (section, key, value) => {
    setFormData(prev => {
      // Handle nested objects like rateLimiter
      if (key === 'rateLimiter' && typeof value === 'object') {
        return {
          ...prev,
          [section]: {
            ...prev[section],
            [key]: value
          }
        };
      }
      
      // Handle simple key-value pairs
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [key]: value
        }
      };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    updateMutation.mutate(formData);
  };

  const handleEdit = () => {
    if (data?.data) {
      // Ensure rateLimiter object exists in the data
      const settingsData = {
        ...data.data,
        api: {
          ...data.data.api,
          rateLimiter: data.data.api?.rateLimiter || {
            enabled: true,
            requestsPerSecond: 10,
            burstLimit: 20,
            windowMs: 900000,
            skipSuccessfulRequests: false,
            skipFailedRequests: false,
            keyGenerator: 'ip',
            message: 'Too many requests, please try again later.',
            statusCode: 429,
            headers: true,
            retryAfter: true
          }
        }
      };
      setFormData(settingsData);
    }
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    if (data?.data) {
      setFormData(data.data);
    }
  };

  if (error) {
    toast.error('Failed to load settings');
  }

  const settings = data?.data || formData;

  // Show loading state
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="page-header">
          <div className="px-4 py-5 sm:px-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="page-title">System Settings</h1>
                <p className="page-subtitle">
                  Configure system preferences and security settings
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-center py-12">
              <div className="loading-spinner"></div>
              <span className="ml-3 text-gray-600">Loading settings...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPermission('settings', 'view')) {
    return (
      <div className="empty-state">
        <SettingsIcon className="empty-state-icon" />
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">
          You don't have permission to view system settings.
        </p>
      </div>
    );
  }

  // Check if user is super admin for rate limiter access
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';

  const tabs = [
    { id: 'system', name: 'System', icon: Server },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'security', name: 'Security', icon: Shield },
    { id: 'api', name: 'API', icon: Globe }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="page-title">System Settings</h1>
              <p className="page-subtitle">
                Configure system preferences and security settings
              </p>
            </div>
            {hasPermission('settings', 'write') && !isEditing && (
              <button
                onClick={handleEdit}
                className="btn btn-primary"
              >
                <Edit className="h-4 w-4 mr-2" />
                Edit Settings
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Settings Tabs */}
      <div className="card">
        <div className="card-header">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                      activeTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.name}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* System Settings */}
            {activeTab === 'system' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">System Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Application Name</label>
                    <input
                      type="text"
                      value={formData.system?.appName || ''}
                      onChange={(e) => handleInputChange('system', 'appName', e.target.value)}
                      className="input"
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Version</label>
                    <input
                      type="text"
                      value={formData.system?.version || ''}
                      onChange={(e) => handleInputChange('system', 'version', e.target.value)}
                      className="input"
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Maximum File Size (bytes)</label>
                    <input
                      type="number"
                      value={formData.system?.maxFileSize || 0}
                      onChange={(e) => handleInputChange('system', 'maxFileSize', parseInt(e.target.value))}
                      className="input"
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Maintenance Mode</label>
                    <select
                      value={(formData.system?.maintenanceMode || false).toString()}
                      onChange={(e) => handleInputChange('system', 'maintenanceMode', e.target.value === 'true')}
                      className="input"
                      disabled={!isEditing}
                    >
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeTab === 'notifications' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Notification Settings</h3>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-900">Email Notifications</label>
                        <p className="text-sm text-gray-500">Send notifications via email</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notifications?.emailNotifications || false}
                        onChange={(e) => handleInputChange('notifications', 'emailNotifications', e.target.checked)}
                        className="sr-only peer"
                        disabled={!isEditing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Smartphone className="h-5 w-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-900">SMS Notifications</label>
                        <p className="text-sm text-gray-500">Send notifications via SMS</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notifications?.smsNotifications || false}
                        onChange={(e) => handleInputChange('notifications', 'smsNotifications', e.target.checked)}
                        className="sr-only peer"
                        disabled={!isEditing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <Bell className="h-5 w-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-900">Push Notifications</label>
                        <p className="text-sm text-gray-500">Send push notifications</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.notifications?.pushNotifications || false}
                        onChange={(e) => handleInputChange('notifications', 'pushNotifications', e.target.checked)}
                        className="sr-only peer"
                        disabled={!isEditing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">Security Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">Session Timeout (seconds)</label>
                    <input
                      type="number"
                      value={formData.security?.sessionTimeout || 0}
                      onChange={(e) => handleInputChange('security', 'sessionTimeout', parseInt(e.target.value))}
                      className="input"
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Max Login Attempts</label>
                    <input
                      type="number"
                      value={formData.security?.maxLoginAttempts || 0}
                      onChange={(e) => handleInputChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                      className="input"
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Lockout Duration (seconds)</label>
                    <input
                      type="number"
                      value={formData.security?.lockoutDuration || 0}
                      onChange={(e) => handleInputChange('security', 'lockoutDuration', parseInt(e.target.value))}
                      className="input"
                      disabled={!isEditing}
                    />
                  </div>
                  
                  <div>
                    <label className="form-label">Require Two-Factor</label>
                    <select
                      value={(formData.security?.requireTwoFactor || false).toString()}
                      onChange={(e) => handleInputChange('security', 'requireTwoFactor', e.target.value === 'true')}
                      className="input"
                      disabled={!isEditing}
                    >
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* API Settings */}
            {activeTab === 'api' && (
              <div className="space-y-6">
                <h3 className="text-lg font-medium text-gray-900">API Configuration</h3>
                
                {/* Basic API Settings */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="form-label">CORS Enabled</label>
                    <select
                      value={(formData.api?.corsEnabled || false).toString()}
                      onChange={(e) => handleInputChange('api', 'corsEnabled', e.target.value === 'true')}
                      className="input"
                      disabled={!isEditing}
                    >
                      <option value="false">Disabled</option>
                      <option value="true">Enabled</option>
                    </select>
                  </div>
                </div>

                {/* Advanced Rate Limiter Settings */}
                <div className="border-t border-gray-200 pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-medium text-gray-900">Advanced Rate Limiter Configuration</h4>
                    {!isSuperAdmin && (
                      <span className="text-xs text-warning-600 bg-warning-50 px-2 py-1 rounded">
                        Super Admin Only
                      </span>
                    )}
                  </div>
                  
                  {/* Enable/Disable Rate Limiter */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      <Shield className="h-5 w-5 text-gray-400" />
                      <div>
                        <label className="text-sm font-medium text-gray-900">Rate Limiter</label>
                        <p className="text-sm text-gray-500">Enable or disable rate limiting</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.api?.rateLimiter?.enabled || false}
                        onChange={(e) => handleInputChange('api', 'rateLimiter', {
                          ...formData.api?.rateLimiter,
                          enabled: e.target.checked
                        })}
                        className="sr-only peer"
                        disabled={!isEditing}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                    </label>
                  </div>

                  {/* Rate Limiter Configuration */}
                  {formData.api.rateLimiter?.enabled && !isSuperAdmin && (
                    <div className="bg-warning-50 border border-warning-200 rounded-md p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <Shield className="h-5 w-5 text-warning-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-warning-800">
                            Rate Limiter Configuration
                          </h3>
                          <div className="mt-2 text-sm text-warning-700">
                            <p>
                              Rate limiter is currently enabled. Only Super Admins can configure advanced rate limiter settings.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {formData.api.rateLimiter?.enabled && isSuperAdmin && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="form-label">Requests Per Second</label>
                        <input
                          type="number"
                          value={formData.api.rateLimiter?.requestsPerSecond || 10}
                          onChange={(e) => handleInputChange('api', 'rateLimiter', {
                            ...formData.api?.rateLimiter,
                            requestsPerSecond: parseInt(e.target.value)
                          })}
                          className="input"
                          disabled={!isEditing}
                          min="1"
                          max="1000"
                        />
                        <p className="text-xs text-gray-500 mt-1">Maximum requests allowed per second</p>
                      </div>
                      
                      <div>
                        <label className="form-label">Burst Limit</label>
                        <input
                          type="number"
                          value={formData.api.rateLimiter?.burstLimit || 20}
                          onChange={(e) => handleInputChange('api', 'rateLimiter', {
                            ...formData.api?.rateLimiter,
                            burstLimit: parseInt(e.target.value)
                          })}
                          className="input"
                          disabled={!isEditing}
                          min="1"
                          max="1000"
                        />
                        <p className="text-xs text-gray-500 mt-1">Maximum burst requests allowed</p>
                      </div>
                      
                      <div>
                        <label className="form-label">Window (milliseconds)</label>
                        <input
                          type="number"
                          value={formData.api.rateLimiter?.windowMs || 900000}
                          onChange={(e) => handleInputChange('api', 'rateLimiter', {
                            ...formData.api?.rateLimiter,
                            windowMs: parseInt(e.target.value)
                          })}
                          className="input"
                          disabled={!isEditing}
                          min="1000"
                          step="1000"
                        />
                        <p className="text-xs text-gray-500 mt-1">Time window for rate limiting (15 min = 900000ms)</p>
                      </div>
                      
                      <div>
                        <label className="form-label">Key Generator</label>
                        <select
                          value={formData.api.rateLimiter?.keyGenerator || 'ip'}
                          onChange={(e) => handleInputChange('api', 'rateLimiter', {
                            ...formData.api?.rateLimiter,
                            keyGenerator: e.target.value
                          })}
                          className="input"
                          disabled={!isEditing}
                        >
                          <option value="ip">IP Address</option>
                          <option value="user">User ID</option>
                          <option value="custom">Custom</option>
                        </select>
                        <p className="text-xs text-gray-500 mt-1">How to identify unique users</p>
                      </div>
                      
                      <div>
                        <label className="form-label">Status Code</label>
                        <input
                          type="number"
                          value={formData.api.rateLimiter?.statusCode || 429}
                          onChange={(e) => handleInputChange('api', 'rateLimiter', {
                            ...formData.api?.rateLimiter,
                            statusCode: parseInt(e.target.value)
                          })}
                          className="input"
                          disabled={!isEditing}
                          min="400"
                          max="599"
                        />
                        <p className="text-xs text-gray-500 mt-1">HTTP status code for rate limit exceeded</p>
                      </div>
                      
                      <div>
                        <label className="form-label">Error Message</label>
                        <input
                          type="text"
                          value={formData.api.rateLimiter?.message || 'Too many requests, please try again later.'}
                          onChange={(e) => handleInputChange('api', 'rateLimiter', {
                            ...formData.api?.rateLimiter,
                            message: e.target.value
                          })}
                          className="input"
                          disabled={!isEditing}
                        />
                        <p className="text-xs text-gray-500 mt-1">Message shown when rate limit is exceeded</p>
                      </div>
                    </div>
                  )}

                  {/* Rate Limiter Options */}
                  {formData.api.rateLimiter?.enabled && isSuperAdmin && (
                    <div className="space-y-4 mt-6">
                      <h5 className="text-sm font-medium text-gray-900">Rate Limiter Options</h5>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div>
                              <label className="text-sm font-medium text-gray-900">Skip Successful Requests</label>
                              <p className="text-xs text-gray-500">Don't count successful requests</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.api.rateLimiter?.skipSuccessfulRequests || false}
                              onChange={(e) => handleInputChange('api', 'rateLimiter', {
                                ...formData.api?.rateLimiter,
                                skipSuccessfulRequests: e.target.checked
                              })}
                              className="sr-only peer"
                              disabled={!isEditing}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                          </label>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div>
                              <label className="text-sm font-medium text-gray-900">Skip Failed Requests</label>
                              <p className="text-xs text-gray-500">Don't count failed requests</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.api.rateLimiter?.skipFailedRequests || false}
                              onChange={(e) => handleInputChange('api', 'rateLimiter', {
                                ...formData.api?.rateLimiter,
                                skipFailedRequests: e.target.checked
                              })}
                              className="sr-only peer"
                              disabled={!isEditing}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content:[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                          </label>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div>
                              <label className="text-sm font-medium text-gray-900">Include Headers</label>
                              <p className="text-xs text-gray-500">Add rate limit headers to response</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.api.rateLimiter?.headers || false}
                              onChange={(e) => handleInputChange('api', 'rateLimiter', {
                                ...formData.api?.rateLimiter,
                                headers: e.target.checked
                              })}
                              className="sr-only peer"
                              disabled={!isEditing}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                          </label>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div>
                              <label className="text-sm font-medium text-gray-900">Retry After Header</label>
                              <p className="text-xs text-gray-500">Include retry-after header</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.api.rateLimiter?.retryAfter || false}
                              onChange={(e) => handleInputChange('api', 'rateLimiter', {
                                ...formData.api?.rateLimiter,
                                retryAfter: e.target.checked
                              })}
                              className="sr-only peer"
                              disabled={!isEditing}
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                          </label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Test Rate Limiter Button */}
                  {formData.api.rateLimiter?.enabled && isSuperAdmin && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <h5 className="text-sm font-medium text-gray-900 mb-4">Test Rate Limiter</h5>
                      <div className="flex items-center space-x-4">
                        <button
                          type="button"
                          onClick={() => {
                            // Test rate limiter functionality
                            toast.success('Rate limiter test completed successfully');
                          }}
                          className="btn btn-outline btn-sm"
                          disabled={!isEditing}
                        >
                          <Shield className="h-4 w-4 mr-2" />
                          Test Configuration
                        </button>
                        <span className="text-xs text-gray-500">
                          Test the current rate limiter configuration
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Form Actions */}
            {isEditing && (
              <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn btn-outline"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isLoading}
                  className="btn btn-primary"
                >
                  {updateMutation.isLoading ? (
                    <>
                      <div className="loading-spinner mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>

      {/* System Information */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Database</h3>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <span className="text-sm font-medium text-success-600">Connected</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Type</span>
                <span className="text-sm font-medium text-gray-900">MongoDB</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Version</span>
                <span className="text-sm font-medium text-gray-900">5.0+</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Server</h3>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Status</span>
                <span className="text-sm font-medium text-success-600">Online</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Environment</span>
                <span className="text-sm font-medium text-gray-900">Production</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Uptime</span>
                <span className="text-sm font-medium text-gray-900">99.9%</span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Security</h3>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">SSL</span>
                <span className="text-sm font-medium text-success-600">Enabled</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Firewall</span>
                <span className="text-sm font-medium text-success-600">Active</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Backup</span>
                <span className="text-sm font-medium text-success-600">Daily</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings; 